import React from 'react';
import { createStyles, makeStyles, Theme, Tooltip } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CheckIcon from '@material-ui/icons/Check';
import folderIcon from '../../assets/img/folder.svg';
import {
  RootFolder,
  Folder,
  isRootFolder,
  FolderContent,
  PanoramaDetails,
} from '../services/UploadManagementServiceTypes';
import { Typography } from '../common/Typography';

export interface PanoramaGallerySelectViewProps {
  panoramas: PanoramaDetails[];
  folders: FolderContent[];
  currentFolder: RootFolder | Folder;
  projectName?: string;
  selectedPanoramaGuids: string[];
  panoramaGuidsInGallery: string[];
  updateSelectedPanoramas: (panorama: PanoramaDetails) => void;
  navigateToFolder: (guid: string | null | undefined) => void;
}

export const PanoramaGallerySelectView: React.FC<PanoramaGallerySelectViewProps> = ({
  panoramas,
  folders,
  currentFolder,
  projectName,
  selectedPanoramaGuids,
  panoramaGuidsInGallery,
  updateSelectedPanoramas,
  navigateToFolder,
}) => {
  const classes = useGallerySelectStyles();

  const isDisabled = (panorama: PanoramaDetails) => {
    return typeof projectName !== 'undefined' && panorama.projectName !== projectName;
  };

  return (
    <div className={classes.content}>
      <div className={classes.folderNav}>
        {!isRootFolder(currentFolder) && (
          <ArrowBackIcon
            onClick={() => navigateToFolder(currentFolder.parentGuid)}
            aria-label='go to parent folder'
          />
        )}
        <Typography className={classes.title} variant='h5' component='span'>
          {currentFolder.name}
        </Typography>
      </div>
      <div className={classes.panoramaSelectBox}>
        {panoramas.map((panorama) => (
          <PanoramaItem
            key={panorama.panoramaGuid}
            panorama={panorama}
            disabled={isDisabled(panorama)}
            isSelected={selectedPanoramaGuids.includes(panorama.panoramaGuid)}
            isInGallery={panoramaGuidsInGallery.includes(panorama.panoramaGuid)}
            onSelect={() => updateSelectedPanoramas(panorama)}
          />
        ))}
        {folders.map((folder) => (
          <FolderItem
            key={folder.guid}
            folder={folder}
            onClick={() => navigateToFolder(folder.guid)}
          />
        ))}
      </div>
    </div>
  );
};

interface PanoramaItemProps {
  panorama: PanoramaDetails;
  disabled: boolean;
  isSelected: boolean;
  isInGallery: boolean;
  onSelect: () => void;
}

export const PanoramaItem: React.FC<PanoramaItemProps> = ({
  panorama,
  disabled,
  isSelected,
  isInGallery,
  onSelect,
}) => {
  const classes = useGallerySelectStyles();

  const item = (
    <div
      className={`${classes.viewItem} ${disabled ? classes.disabled : ''}`}
      onClick={() => !disabled && onSelect()}
      role='treeitem'
      aria-label='panorama'
      aria-selected={isSelected}
    >
      {(isSelected || isInGallery) && (
        <CheckIcon
          aria-label={isSelected ? 'selected' : 'not selected'}
          className={`${classes.checkIcon} ${
            !isSelected && isInGallery ? classes.preSelected : ''
          }`}
        />
      )}
      <img src={panorama.thumbnailDownloadUrl} alt={panorama.title} className={classes.thumbnail} />
      <Typography>{panorama.title}</Typography>
    </div>
  );
  if (disabled) {
    return (
      <Tooltip
        classes={{ tooltip: classes.tooltip }}
        title={'You can only select panoramas exported from the same project.'}
      >
        {item}
      </Tooltip>
    );
  }
  return item;
};

interface FolderItemProps {
  folder: FolderContent;
  onClick: () => void;
}

export const FolderItem: React.FC<FolderItemProps> = ({ folder, onClick }) => {
  const classes = useGallerySelectStyles();

  return (
    <div className={classes.viewItem} onClick={onClick}>
      <img src={folderIcon} alt={folder.name} className={classes.thumbnail} />
      <Typography>{folder.name}</Typography>
    </div>
  );
};

export const useGallerySelectStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      fontWeight: 'bold',
    },
    content: {
      maxHeight: 360,
      border: '1px solid rgba(0, 0, 0, 0.12)',
      padding: theme.spacing(1),
    },
    folderNav: {
      position: 'relative',
      paddingLeft: theme.spacing(4),
      marginBottom: theme.spacing(1),

      '& > svg': {
        position: 'absolute',
        top: 0,
        left: 0,
        fontSize: 24,
        cursor: 'pointer',

        '&:hover': {
          fill: theme.palette.primary.main,
        },
      },
    },
    panoramaSelectBox: {
      display: 'flex',
      flexWrap: 'wrap',
      overflow: 'auto',
      maxHeight: 310,

      '& > *': {
        margin: theme.spacing(1),
      },
    },
    viewItem: {
      position: 'relative',
      padding: theme.spacing(1),
      border: '1px solid #787878',
      cursor: 'pointer',
      width: 133,
      transition: '0.3s',

      '& > p': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },

      '&:hover': {
        borderColor: theme.palette.primary.main,
        background: theme.palette.primary.light,
      },
    },
    disabled: {
      border: '1px solid #A3A3A3',
      color: '#A3A3A3',
      cursor: 'default',

      '&:hover': {
        borderColor: 'initial',
        background: 'initial',
      },

      '& > img': {
        filter: 'grayscale(100%)',
      },
    },
    tooltip: {
      fontSize: 11,
      marginTop: 8,
    },
    thumbnail: {
      width: 115,
      height: 68,
      display: 'block',
      margin: '0 auto',
      marginBottom: theme.spacing(1),
    },
    checkIcon: {
      position: 'absolute',
      top: 11,
      right: 11,
      width: 18,
      height: 18,
      background: theme.palette.primary.main,
      fill: '#ffffff',
      borderRadius: 2,
    },
    preSelected: {
      background: '#1B2042',
    },
  }),
);
